import React, { useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  FaBaby,
  FaPeopleRoof,
  FaPersonPregnant,
  FaUserDoctor,
  FaUserPen,
} from 'react-icons/fa6';
import {
  FiAlertTriangle,
  FiChevronDown,
  FiChevronRight,
  FiInfo,
} from 'react-icons/fi';

import { usePatientDossier } from '../../../helpers/api/patients/patients';
import { computeGAFromLmp } from '../../ComponnentsUI/NewCaseReverseUpload/utils';
import {
  ABORTION_MANAGEMENT_OPTIONS,
  DELIVERY_PLACE_OPTIONS,
  DELIVERY_TYPE_OPTIONS,
  DISABILITY_OPTIONS,
  EXTRACTION_MODALITY_OPTIONS,
  FAMILY_CONDITIONS,
  FAMILY_MEMBER_OPTIONS,
  IUFD_EXPULSION_OPTIONS,
  MATERNAL_COMPLICATION_OPTIONS,
  MEDICAL_COVERAGE_OPTIONS,
  NEONATAL_COMPLICATION_OPTIONS,
  NEWBORN_SEX_OPTIONS,
  NEWBORN_VITAL_STATUS_OPTIONS,
  OBSTETRIC_ANOMALY_TYPES,
  PREGNANCY_RISK_OPTIONS,
  PREGNANCY_TERM_STATUS_OPTIONS,
  WOMAN_ORIGIN_OPTIONS,
} from '../../ComponnentsUI/features/AddPatientForm/constants';

const codeLabel = <T extends string>(
  options: { value: T; labelKey: string }[],
  code: string | null | undefined,
  t: (k: string) => string
): string => {
  if (!code) {
    return '—';
  }
  const hit = options.find((o) => o.value === code);
  return hit ? t(hit.labelKey) : code;
};

type FamilyNotesPayload = {
  members?: string[];
  entries?: { label?: string; members?: string[] }[];
};
const decodeFamilyNotes = (raw: unknown): FamilyNotesPayload | null => {
  if (raw == null) {
    return null;
  }
  if (typeof raw !== 'string') {
    return raw && typeof raw === 'object' ? (raw as FamilyNotesPayload) : null;
  }
  const trimmed = raw.trim();
  if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) {
    return null;
  }
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object') {
      return parsed as FamilyNotesPayload;
    }
    return null;
  } catch {
    return null;
  }
};

type AnomalyDescriptionPayload = {
  free?: string;
  ga_days?: number | null;
  anomaly_count?: number | null;
};
const decodeAnomalyDescription = (
  raw: string | null | undefined
): AnomalyDescriptionPayload => {
  if (!raw) {
    return {};
  }
  const trimmed = raw.trim();
  if (!trimmed.startsWith('{')) {
    return { free: trimmed };
  }
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object') {
      return parsed as AnomalyDescriptionPayload;
    }
  } catch {
    /* legacy plain text — fall through */
  }
  return { free: trimmed };
};

const formatDate = (value: unknown): string | null => {
  if (value == null || value === '') {
    return null;
  }
  const d = dayjs(value as string | Date);
  return d.isValid() ? d.format('MMM D, YYYY') : null;
};

export type PregnancySnapshot = Record<string, unknown>;

export const asNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

export const isPregnancyActiveForExam = (
  pregnancy: PregnancySnapshot | null | undefined
): boolean => {
  if (!pregnancy || pregnancy.id == null) {
    return false;
  }

  const status = String(pregnancy.pregnancy_status ?? '')
    .trim()
    .toLowerCase();
  if (status && status !== 'active') {
    return false;
  }

  if (pregnancy.is_medically_active === false) {
    return false;
  }

  return true;
};

export const derivePrenatalStudyCategory = (
  pregnancy: PregnancySnapshot | null | undefined
): 'T1' | 'T2' | 'T3' | 'NOT_DEFINED' => {
  if (!pregnancy) {
    return 'NOT_DEFINED';
  }

  let weeks =
    asNumber(pregnancy.current_gestational_age_weeks) ??
    asNumber(pregnancy.gestational_age_weeks);

  if (weeks == null && typeof pregnancy.last_menstrual_period === 'string') {
    const computed = computeGAFromLmp(
      pregnancy.last_menstrual_period,
      typeof pregnancy.ultrasound_date === 'string'
        ? pregnancy.ultrasound_date
        : undefined
    );
    weeks = computed?.weeks ?? null;
  }

  if (weeks == null) {
    return 'NOT_DEFINED';
  }

  if (weeks < 13) {
    return 'T1';
  }
  if (weeks < 27) {
    return 'T2';
  }
  return 'T3';
};

const cardCls =
  'rounded-2xl bg-card-color-100 ring-1 ring-slate-200/80 shadow-sm dark:bg-gray-800 dark:ring-slate-700';

const SectionCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  collapsible?: boolean;
  defaultExpanded?: boolean;
}> = ({
  icon,
  title,
  description,
  action,
  children,
  collapsible = false,
  defaultExpanded = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(
    collapsible ? defaultExpanded : true
  );

  if (!collapsible) {
    return (
      <section className={`${cardCls} shrink-0`}>
        <header className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-5 py-4 dark:border-slate-700">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-dui/10 text-primary-dui dark:bg-primary-dui/20 dark:text-primary-light">
              {icon}
            </span>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold tracking-tight text-title-page-color-100 dark:text-white">
                {title}
              </h3>
              {description ? (
                <p className="mt-0.5 text-xs text-title-page-color-300 dark:text-gray-400">
                  {description}
                </p>
              ) : null}
            </div>
          </div>
          {action}
        </header>
        <div className="px-5 py-4">{children}</div>
      </section>
    );
  }

  return (
    <section className={`${cardCls} shrink-0`}>
      <button
        type="button"
        className={`flex w-full flex-wrap items-start justify-between gap-3 px-5 py-4 text-left transition-colors hover:bg-card-color-200/30 dark:hover:bg-gray-900/30 ${
          isExpanded ? 'border-b border-slate-100 dark:border-slate-700' : ''
        }`}
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
      >
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-dui/10 text-primary-dui dark:bg-primary-dui/20 dark:text-primary-light">
            {icon}
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold tracking-tight text-title-page-color-100 dark:text-white">
              {title}
            </h3>
            {description ? (
              <p className="mt-0.5 text-xs text-title-page-color-300 dark:text-gray-400">
                {description}
              </p>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 items-start gap-2">
          {action ? (
            <div className="flex flex-wrap items-center justify-end gap-1.5">
              {action}
            </div>
          ) : null}
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-card-color-200 text-title-page-color-300 ring-1 ring-slate-200/70 dark:bg-gray-900/60 dark:text-gray-300 dark:ring-slate-700">
            {isExpanded ? (
              <FiChevronDown className="h-4 w-4" />
            ) : (
              <FiChevronRight className="h-4 w-4" />
            )}
          </span>
        </div>
      </button>
      {isExpanded ? <div className="px-5 py-4">{children}</div> : null}
    </section>
  );
};

const KeyValueGrid: React.FC<{
  rows: { label: string; value: React.ReactNode | string | null | undefined }[];
  maxCols?: 2 | 3 | 4;
}> = ({ rows, maxCols = 2 }) => {
  const { t } = useTranslation('Patient');
  const colCls =
    maxCols === 4
      ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
      : maxCols === 3
        ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
        : 'grid-cols-1 sm:grid-cols-2';
  return (
    <dl className={`grid gap-3 ${colCls}`}>
      {rows.map(({ label, value }) => {
        const isEmpty =
          value == null ||
          value === '' ||
          value === '—' ||
          (typeof value === 'string' && value.trim() === '');
        return (
          <div
            key={label}
            className="flex min-w-0 flex-col rounded-lg bg-card-color-200/60 px-3 py-2 dark:bg-gray-900/40"
          >
            <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-title-page-color-300 dark:text-gray-400">
              {label}
            </dt>
            <dd
              className={`mt-1 truncate text-sm font-medium ${
                isEmpty
                  ? 'italic text-title-page-color-300 dark:text-gray-500'
                  : 'text-title-page-color-100 dark:text-gray-100'
              }`}
              title={typeof value === 'string' ? value : undefined}
            >
              {isEmpty ? t('notProvided') : (value as React.ReactNode)}
            </dd>
          </div>
        );
      })}
    </dl>
  );
};

const Pill: React.FC<{
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info';
  children: React.ReactNode;
}> = ({ tone = 'neutral', children }) => {
  const tones: Record<string, string> = {
    neutral: 'bg-slate-100 text-slate-700 dark:bg-zinc-800 dark:text-zinc-200',
    success:
      'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200',
    warning:
      'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
    danger: 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-200',
    info: 'bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-200',
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${tones[tone]}`}
    >
      {children}
    </span>
  );
};

const EmptyState: React.FC<{ icon?: React.ReactNode; message: string }> = ({
  icon,
  message,
}) => (
  <div className="flex flex-col items-center justify-center gap-2 rounded-lg bg-card-color-200/60 px-4 py-6 text-center text-xs text-title-page-color-300 dark:bg-gray-900/40 dark:text-gray-400">
    {icon ?? <FiInfo className="h-4 w-4" />}
    <p>{message}</p>
  </div>
);

// --------------------------------------------------------------------------
// Subsections
// --------------------------------------------------------------------------

interface PatientLike extends Record<string, unknown> {
  patient_name?: string;
  patient_last_name?: string;
  patient_middle_name?: string;
  birth_date?: string | null;
  age_years?: number | null;
  cin?: string | null;
  address?: string | null;
  phone_number?: string | null;
  email?: string | null;
  occupation?: string | null;
  education_level?: string | null;
  marital_status?: string | null;
  spouse_name?: string | null;
  spouse_occupation?: string | null;
  consanguinity?: boolean | null;
  woman_origin?: string | null;
  coverage_mode?: string | null;
  medical_coverage_label?: string | null;
  habit_caffeine?: boolean | null;
  habit_alcohol?: boolean | null;
  habit_tobacco?: boolean | null;
  habit_medications?: boolean | null;
  habit_geophagy?: boolean | null;
  habit_other_notes?: string | null;
  blood_group_abo?: string | null;
  blood_group_rhesus?: string | null;
  husband_blood_group_abo?: string | null;
  husband_blood_group_rhesus?: string | null;
  weight_kg?: number | null;
  height_cm?: number | null;
  pregnancy?: Record<string, unknown> | null;
}

const DemographicsCard: React.FC<{ patient: PatientLike }> = ({ patient }) => {
  const { t } = useTranslation('Patient');

  const computedAge = (() => {
    if (patient.age_years != null) {
      return patient.age_years;
    }
    if (!patient.birth_date) {
      return null;
    }
    const bd = dayjs(patient.birth_date);
    return bd.isValid() ? Math.max(0, dayjs().diff(bd, 'year')) : null;
  })();

  const educationLabel = (() => {
    if (!patient.education_level) {
      return null;
    }
    const map: Record<string, string> = {
      without: 'educationWithout',
      primary: 'educationPrimary',
      secondary: 'educationSecondary',
      university: 'educationUniversity',
    };
    const key = map[patient.education_level];
    return key ? t(key) : patient.education_level;
  })();

  const maritalLabel = (() => {
    if (!patient.marital_status) {
      return null;
    }
    const map: Record<string, string> = {
      SINGLE: 'dossier_marital_single',
      MARRIED: 'dossier_marital_married',
      WIDOWED: 'dossier_marital_widowed',
      DIVORCED: 'dossier_marital_divorced',
    };
    const key = map[patient.marital_status];
    return key ? t(key) : patient.marital_status;
  })();

  const coverageLabel = (() => {
    if (!patient.coverage_mode) {
      return null;
    }
    const map: Record<string, string> = {
      FIXE: 'dossier_coverage_fixe',
      MOBILE: 'dossier_coverage_mobile',
    };
    const key = map[patient.coverage_mode];
    return key ? t(key) : patient.coverage_mode;
  })();

  const medicalCoverageLabel = (() => {
    const raw = patient.medical_coverage_label;
    if (!raw) {
      return null;
    }
    const match = MEDICAL_COVERAGE_OPTIONS.find((o) => o.value === raw);
    return match ? t(match.labelKey) : raw;
  })();

  const originLabel = patient.woman_origin
    ? codeLabel(WOMAN_ORIGIN_OPTIONS, patient.woman_origin, t)
    : null;

  return (
    <SectionCard
      icon={<FaUserPen className="h-4 w-4" />}
      title={t('details_section_title')}
      description={t('details_section_helper')}
    >
      <KeyValueGrid
        maxCols={4}
        rows={[
          {
            label: t('dossier_full_name'),
            value:
              [patient.patient_last_name, patient.patient_name]
                .filter(Boolean)
                .join(' ') || '—',
          },
          {
            label: t('dossier_age_label'),
            value:
              computedAge != null
                ? `${computedAge} ${t('dossier_age_suffix')}`
                : null,
          },
          {
            label: t('dossier_dob_short'),
            value: formatDate(patient.birth_date),
          },
          { label: 'CIN', value: patient.cin || null },
          {
            label: t('dossier_origin'),
            value: originLabel,
          },
          { label: t('dossier_marital_short'), value: maritalLabel },
          {
            label: t('dossier_consanguinity_short'),
            value:
              patient.consanguinity == null
                ? null
                : patient.consanguinity
                  ? t('yesShort')
                  : t('noShort'),
          },
          {
            label: t('dossier_spouse_name'),
            value: patient.spouse_name || null,
          },
          {
            label: t('dossier_spouse_occupation'),
            value: patient.spouse_occupation || null,
          },
          { label: t('occupation'), value: patient.occupation || null },
          {
            label: t('educationLevel'),
            value: educationLabel,
          },
          {
            label: t('dossier_medical_coverage'),
            value: medicalCoverageLabel,
          },
          { label: t('dossier_coverage_mode'), value: coverageLabel },
          { label: t('dossier_address_short'), value: patient.address || null },
          { label: t('dossier_phone'), value: patient.phone_number || null },
          { label: t('dossier_email_short'), value: patient.email || null },
        ]}
      />
    </SectionCard>
  );
};

const HabitsCard: React.FC<{ patient: PatientLike }> = ({ patient }) => {
  const { t } = useTranslation('Patient');
  const habits: {
    key: string;
    labelKey: string;
    value: boolean | null | undefined;
  }[] = [
    {
      key: 'caffeine',
      labelKey: 'dossier_habit_caffeine',
      value: patient.habit_caffeine,
    },
    {
      key: 'alcohol',
      labelKey: 'dossier_habit_alcohol',
      value: patient.habit_alcohol,
    },
    {
      key: 'tobacco',
      labelKey: 'dossier_habit_tobacco',
      value: patient.habit_tobacco,
    },
    {
      key: 'medications',
      labelKey: 'dossier_habit_medications',
      value: patient.habit_medications,
    },
    {
      key: 'geophagy',
      labelKey: 'dossier_habit_geophagy',
      value: patient.habit_geophagy,
    },
  ];
  const positives = habits.filter((h) => h.value === true);
  const notes = (patient.habit_other_notes || '').trim();
  const isEmpty = positives.length === 0 && !notes;

  return (
    <SectionCard
      icon={<span className="text-base">🍃</span>}
      title={t('details_habits_title')}
    >
      {isEmpty ? (
        <EmptyState message={t('details_habits_empty')} />
      ) : (
        <div className="flex flex-col gap-3">
          {positives.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {positives.map((h) => (
                <Pill key={h.key} tone="warning">
                  {t(h.labelKey)}
                </Pill>
              ))}
            </div>
          ) : null}
          {notes ? (
            <p className="text-xs text-title-page-color-300 dark:text-gray-400">
              {notes}
            </p>
          ) : null}
        </div>
      )}
    </SectionCard>
  );
};

const formatBloodGroup = (
  abo: string | null | undefined,
  rhesus: string | null | undefined
): string => {
  const aboText = abo ? abo : '';
  let rhesusText = '';
  if (rhesus === 'POSITIVE') {
    rhesusText = '+';
  } else if (rhesus === 'NEGATIVE') {
    rhesusText = '-';
  }
  const combined = `${aboText}${rhesusText}`.trim();
  return combined || '—';
};

const BloodGroupCard: React.FC<{ patient: PatientLike }> = ({ patient }) => {
  const { t } = useTranslation('Patient');
  return (
    <SectionCard
      icon={<span className="text-base">🩸</span>}
      title={t('details_blood_group_title')}
    >
      <KeyValueGrid
        maxCols={2}
        rows={[
          {
            label: t('details_blood_group_patient_short'),
            value: formatBloodGroup(
              patient.blood_group_abo,
              patient.blood_group_rhesus
            ),
          },
          {
            label: t('details_blood_group_husband_short'),
            value: formatBloodGroup(
              patient.husband_blood_group_abo,
              patient.husband_blood_group_rhesus
            ),
          },
        ]}
      />
    </SectionCard>
  );
};

const AnthropometryCard: React.FC<{ patient: PatientLike }> = ({ patient }) => {
  const { t } = useTranslation('Patient');
  const weight =
    typeof patient.weight_kg === 'number' && Number.isFinite(patient.weight_kg)
      ? `${patient.weight_kg} kg`
      : null;
  const height =
    typeof patient.height_cm === 'number' && Number.isFinite(patient.height_cm)
      ? `${patient.height_cm} cm`
      : null;
  return (
    <SectionCard
      icon={<span className="text-base">📏</span>}
      title={t('details_anthropometry_title')}
    >
      <KeyValueGrid
        maxCols={2}
        rows={[
          { label: t('details_weight'), value: weight },
          { label: t('details_height'), value: height },
        ]}
      />
    </SectionCard>
  );
};

// --------------------------------------------------------------------------

interface FamilyHistoryRow {
  condition_code?: string;
  wife_family_notes?: string | null;
  husband_family_notes?: string | null;
}

const decodeMembers = (
  raw: string | null | undefined,
  t: (k: string) => string
): string[] => {
  const decoded = decodeFamilyNotes(raw);
  if (!decoded) {
    return [];
  }
  const codes = Array.isArray(decoded.members) ? decoded.members : [];
  return codes
    .map((c) => codeLabel(FAMILY_MEMBER_OPTIONS, c, t))
    .filter((s): s is string => Boolean(s) && s !== '—');
};

interface FamilyHistoryCardProps {
  rows: FamilyHistoryRow[];
  isLoading?: boolean;
  error?: unknown;
}

const FamilyHistoryCard: React.FC<FamilyHistoryCardProps> = ({
  rows,
  isLoading,
  error,
}) => {
  const { t } = useTranslation('Patient');

  const byCode: Record<string, FamilyHistoryRow> = {};
  rows.forEach((r) => {
    if (r.condition_code) {
      byCode[r.condition_code] = r;
    }
  });
  const otherRow = byCode.OTHER;
  const otherEntries = (() => {
    if (!otherRow) {
      return [] as { label: string; wife: string[]; husband: string[] }[];
    }
    const wifeOther = decodeFamilyNotes(otherRow.wife_family_notes);
    const husbandOther = decodeFamilyNotes(otherRow.husband_family_notes);
    const labels = new Set<string>();
    (wifeOther?.entries ?? []).forEach((e) => e?.label && labels.add(e.label));
    (husbandOther?.entries ?? []).forEach(
      (e) => e?.label && labels.add(e.label)
    );
    return Array.from(labels).map((label) => {
      const w = wifeOther?.entries?.find((e) => e?.label === label);
      const h = husbandOther?.entries?.find((e) => e?.label === label);
      return {
        label,
        wife: (w?.members ?? []).map((c) =>
          codeLabel(FAMILY_MEMBER_OPTIONS, c, t)
        ),
        husband: (h?.members ?? []).map((c) =>
          codeLabel(FAMILY_MEMBER_OPTIONS, c, t)
        ),
      };
    });
  })();

  return (
    <SectionCard
      icon={<FaPeopleRoof className="h-4 w-4" />}
      title={t('details_family_section_title')}
      description={t('details_family_section_helper')}
      collapsible
      defaultExpanded={false}
    >
      {isLoading ? (
        <EmptyState message={t('details_family_loading')} />
      ) : error ? (
        <EmptyState
          icon={<FiAlertTriangle className="h-4 w-4 text-amber-500" />}
          message={t('details_family_error')}
        />
      ) : rows.length === 0 ? (
        <EmptyState message={t('details_family_empty')} />
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {FAMILY_CONDITIONS.map(({ code, labelKey, icon }) => {
            const row = byCode[code];
            const wifeMembers = decodeMembers(row?.wife_family_notes, t);
            const husbandMembers = decodeMembers(row?.husband_family_notes, t);
            const noData =
              wifeMembers.length === 0 && husbandMembers.length === 0;
            return (
              <div
                key={code}
                className="rounded-xl border border-slate-200/80 bg-card-color-200/50 p-3 dark:border-slate-700 dark:bg-gray-900/40"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span aria-hidden>{icon}</span>
                    <p className="text-sm font-semibold text-title-page-color-100 dark:text-white">
                      {t(labelKey)}
                    </p>
                  </div>
                  {noData ? (
                    <Pill tone="neutral">{t('details_family_no_pill')}</Pill>
                  ) : (
                    <Pill tone="warning">
                      {t('details_family_reported_pill')}
                    </Pill>
                  )}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-title-page-color-300 dark:text-gray-400">
                      {t('dossier_wife_side')}
                    </p>
                    <p className="mt-1 text-title-page-color-100 dark:text-gray-200">
                      {wifeMembers.length ? wifeMembers.join(', ') : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-title-page-color-300 dark:text-gray-400">
                      {t('dossier_husband_side')}
                    </p>
                    <p className="mt-1 text-title-page-color-100 dark:text-gray-200">
                      {husbandMembers.length ? husbandMembers.join(', ') : '—'}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
          {otherEntries.length > 0 ? (
            <div className="rounded-xl border border-slate-200/80 bg-card-color-200/50 p-3 dark:border-slate-700 dark:bg-gray-900/40 md:col-span-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span aria-hidden>📝</span>
                  <p className="text-sm font-semibold text-title-page-color-100 dark:text-white">
                    {t('details_family_other_conditions')}
                  </p>
                </div>
                <Pill tone="info">
                  {t('details_family_other_count', {
                    count: otherEntries.length,
                  })}
                </Pill>
              </div>
              <ul className="mt-3 space-y-2 text-xs">
                {otherEntries.map((e) => (
                  <li
                    key={e.label}
                    className="rounded-md bg-card-color-100 px-3 py-2 ring-1 ring-slate-200/70 dark:bg-gray-800 dark:ring-slate-600"
                  >
                    <p className="font-semibold text-title-page-color-100 dark:text-white">
                      {e.label}
                    </p>
                    <p className="mt-1 text-title-page-color-300 dark:text-gray-400">
                      {t('details_family_woman_label')}:{' '}
                      {e.wife.length ? e.wife.join(', ') : '—'} ·{' '}
                      {t('details_family_husband_label')}:{' '}
                      {e.husband.length ? e.husband.join(', ') : '—'}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}
    </SectionCard>
  );
};

interface MedicalHistoryRow {
  medical_hx?: string | null;
  surgical_hx?: string | null;
  gynecological_hx?: string | null;
  disability_type?: string | null;
  disability_other_description?: string | null;
  vat_dose?: number | null;
  rubella_vaccinated?: boolean | null;
  rubella_vaccination_date?: string | null;
  hepatitis_b_vaccinated?: boolean | null;
  hepatitis_b_vaccination_date?: string | null;
  cervical_smear_iva_date?: string | null;
}

const splitTextEntries = (raw: string | null | undefined): string[] =>
  (raw ?? '')
    .split(/\r?\n|\u2022|;/g)
    .map((s) => s.trim())
    .filter(Boolean);

interface WomansHistoryCardProps {
  row: MedicalHistoryRow;
  isLoading?: boolean;
  error?: unknown;
}

const WomansHistoryCard: React.FC<WomansHistoryCardProps> = ({
  row,
  isLoading,
  error,
}) => {
  const { t } = useTranslation('Patient');

  const disabilityCodes = (row.disability_type ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const medical = splitTextEntries(row.medical_hx);
  const surgical = splitTextEntries(row.surgical_hx);
  const gyneco = splitTextEntries(row.gynecological_hx);
  const hasPreventiveCare =
    row.vat_dose != null ||
    row.rubella_vaccinated != null ||
    Boolean(row.rubella_vaccination_date) ||
    row.hepatitis_b_vaccinated != null ||
    Boolean(row.hepatitis_b_vaccination_date) ||
    Boolean(row.cervical_smear_iva_date);

  const hasAny =
    medical.length > 0 ||
    surgical.length > 0 ||
    gyneco.length > 0 ||
    disabilityCodes.length > 0 ||
    Boolean(row.disability_other_description) ||
    hasPreventiveCare;

  const vaccinationValue = (
    vaccinated: boolean | null | undefined,
    date: string | null | undefined
  ): string => {
    if (vaccinated == null) {
      return t('notProvided');
    }
    if (!vaccinated) {
      return t('no');
    }
    const formattedDate = formatDate(date);
    return formattedDate
      ? `${t('yes')} · ${formattedDate}`
      : t('yes');
  };

  return (
    <SectionCard
      icon={<FaUserDoctor className="h-4 w-4" />}
      title={t('details_clinical_section_title')}
      description={t('details_clinical_section_helper')}
      collapsible
      defaultExpanded={false}
    >
      {isLoading ? (
        <EmptyState message={t('details_clinical_loading')} />
      ) : error ? (
        <EmptyState
          icon={<FiAlertTriangle className="h-4 w-4 text-amber-500" />}
          message={t('details_clinical_error')}
        />
      ) : !hasAny ? (
        <EmptyState message={t('details_clinical_empty')} />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {[
              {
                title: t('details_clinical_medical_label'),
                items: medical,
                icon: '🩺',
              },
              {
                title: t('details_clinical_surgical_label'),
                items: surgical,
                icon: '🔪',
              },
              {
                title: t('details_clinical_gynecological_label'),
                items: gyneco,
                icon: '🌸',
              },
            ].map(({ title, items, icon }) => (
              <div
                key={title}
                className="rounded-xl border border-slate-200/80 bg-card-color-200/50 p-3 dark:border-slate-700 dark:bg-gray-900/40"
              >
                <p className="flex items-center gap-1.5 text-xs font-semibold text-title-page-color-100 dark:text-white">
                  <span aria-hidden>{icon}</span>
                  {t('details_clinical_history_suffix', { title })}
                </p>
                {items.length === 0 ? (
                  <p className="mt-2 text-xs italic text-title-page-color-300 dark:text-gray-500">
                    {t('notProvided')}
                  </p>
                ) : (
                  <ul className="mt-2 space-y-1.5 text-xs text-title-page-color-100 dark:text-gray-200">
                    {items.map((entry, index) => (
                      <li key={index} className="flex gap-1.5">
                        <span className="text-primary-dui">•</span>
                        <span className="break-words">{entry}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-slate-200/80 bg-card-color-200/50 p-3 dark:border-slate-700 dark:bg-gray-900/40">
            <p className="text-xs font-semibold text-title-page-color-100 dark:text-white">
              {t('details_clinical_disabilities')}
            </p>
            {disabilityCodes.length === 0 ? (
              <p className="mt-1 text-xs italic text-title-page-color-300 dark:text-gray-500">
                {t('details_clinical_none_reported')}
              </p>
            ) : (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {disabilityCodes.map((code) => (
                  <Pill key={code} tone="neutral">
                    {codeLabel(DISABILITY_OPTIONS, code, t)}
                  </Pill>
                ))}
              </div>
            )}
            {row.disability_other_description ? (
              <p className="mt-2 text-xs text-title-page-color-300 dark:text-gray-400">
                {row.disability_other_description}
              </p>
            ) : null}
          </div>

          {hasPreventiveCare ? (
            <div className="rounded-xl border border-slate-200/80 bg-card-color-200/50 p-3 dark:border-slate-700 dark:bg-gray-900/40">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-title-page-color-100 dark:text-white">
                <span aria-hidden>💉</span>
                {t('details_preventive_care_title')}
              </p>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  {
                    label: t('details_preventive_vat_dose'),
                    value:
                      row.vat_dose != null
                        ? t('details_preventive_vat_dose_value', {
                            dose: row.vat_dose,
                          })
                        : t('notProvided'),
                  },
                  {
                    label: t('details_preventive_rubella'),
                    value: vaccinationValue(
                      row.rubella_vaccinated,
                      row.rubella_vaccination_date
                    ),
                  },
                  {
                    label: t('details_preventive_hepatitis_b'),
                    value: vaccinationValue(
                      row.hepatitis_b_vaccinated,
                      row.hepatitis_b_vaccination_date
                    ),
                  },
                  {
                    label: t('details_preventive_cervical_smear_iva'),
                    value:
                      formatDate(row.cervical_smear_iva_date) ??
                      t('notProvided'),
                  },
                ].map(({ label, value }) => (
                  <div
                    key={label}
                    className="rounded-lg bg-card-color-100 px-3 py-2.5 ring-1 ring-slate-200/70 dark:bg-gray-800 dark:ring-slate-600"
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-title-page-color-300 dark:text-gray-400">
                      {label}
                    </p>
                    <p className="mt-1 text-xs font-medium text-title-page-color-100 dark:text-gray-200">
                      {value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </SectionCard>
  );
};

interface PriorDeliveryApiRow {
  id?: number;
  delivery_index?: number;
  delivery_date?: string | null;
  delivery_type?: string | null;
  delivery_ga_weeks?: number | null;
  delivery_ga_days?: number | null;
  pregnancy_term_status?: string | null;
  extraction_modalities?: string | null;
  cesarean_indication?: string | null;
  delivery_place?: string | null;
  delivery_complication_type?: string | null;
  newborn_complication_type?: string | null;
  newborn_weight_grams?: number | null;
  newborn_weights_note?: string | null;
  newborn_sex?: string | null;
  newborn_vital_status?: string | null;
  has_obstetric_anomalies?: boolean | null;
  anomaly_type?: string | null;
  anomaly_count?: number | null;
  anomaly_date?: string | null;
  anomaly_place?: string | null;
  anomaly_ga_weeks?: number | null;
  anomaly_description?: string | null;
}

const formatGa = (
  weeks: number | null | undefined,
  days: number | null | undefined
): string | null => {
  if (weeks == null) {
    return null;
  }
  return `${weeks}+${days ?? 0} w`;
};

const labelForExtractionLike = (
  row: PriorDeliveryApiRow,
  t: (k: string) => string
): string | null => {
  const code = row.extraction_modalities;
  if (!code) {
    return null;
  }
  if (row.has_obstetric_anomalies && row.anomaly_type) {
    if (row.anomaly_type === 'AVORTEMENT') {
      return codeLabel(ABORTION_MANAGEMENT_OPTIONS, code, t);
    }
    if (row.anomaly_type === 'IUFD') {
      return codeLabel(IUFD_EXPULSION_OPTIONS, code, t);
    }
    return codeLabel(EXTRACTION_MODALITY_OPTIONS, code, t);
  }
  return codeLabel(EXTRACTION_MODALITY_OPTIONS, code, t);
};

const PriorDeliveryRowCard: React.FC<{ row: PriorDeliveryApiRow }> = ({
  row,
}) => {
  const { t } = useTranslation('Patient');
  const isAnomaly = !!row.has_obstetric_anomalies;
  const dateLabel = formatDate(
    isAnomaly ? row.anomaly_date : row.delivery_date
  );
  const placeLabel = isAnomaly
    ? row.anomaly_place || null
    : row.delivery_place
      ? codeLabel(DELIVERY_PLACE_OPTIONS, row.delivery_place, t)
      : null;
  const gaLabel = isAnomaly
    ? formatGa(row.anomaly_ga_weeks, null)
    : formatGa(row.delivery_ga_weeks, row.delivery_ga_days);
  const description = decodeAnomalyDescription(row.anomaly_description);

  return (
    <article
      className={`rounded-xl border p-4 transition-colors ${
        isAnomaly
          ? 'border-amber-200/80 bg-amber-50/40 dark:border-amber-900/40 dark:bg-amber-950/20'
          : 'border-slate-200/80 bg-card-color-200/50 dark:border-slate-700 dark:bg-gray-900/40'
      }`}
    >
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span
            className={`flex h-7 w-7 items-center justify-center rounded-md text-xs font-semibold ${
              isAnomaly
                ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                : 'bg-primary-dui/10 text-primary-dui dark:bg-primary-dui/20 dark:text-primary-light'
            }`}
          >
            #{row.delivery_index ?? '?'}
          </span>
          <div>
            <p className="text-sm font-semibold text-title-page-color-100 dark:text-white">
              {isAnomaly
                ? t('details_prior_row_pregnancy_prefix', {
                    type: codeLabel(
                      OBSTETRIC_ANOMALY_TYPES,
                      row.anomaly_type,
                      t
                    ),
                  })
                : t('details_prior_row_delivery_prefix', {
                    type: codeLabel(
                      DELIVERY_TYPE_OPTIONS,
                      row.delivery_type,
                      t
                    ),
                  })}
            </p>
            {dateLabel ? (
              <p className="text-xs text-title-page-color-300 dark:text-gray-400">
                {dateLabel}
              </p>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {gaLabel ? (
            <Pill tone="info">
              {t('details_prior_row_ga_prefix', { ga: gaLabel })}
            </Pill>
          ) : null}
          {!isAnomaly && row.pregnancy_term_status ? (
            <Pill
              tone={
                row.pregnancy_term_status === 'TERM'
                  ? 'success'
                  : row.pregnancy_term_status === 'POSTTERM'
                    ? 'warning'
                    : 'info'
              }
            >
              {codeLabel(
                PREGNANCY_TERM_STATUS_OPTIONS,
                row.pregnancy_term_status,
                t
              )}
            </Pill>
          ) : null}
          {isAnomaly ? (
            <Pill tone="warning">{t('details_prior_row_anomaly_badge')}</Pill>
          ) : (
            <Pill tone="success">
              {t('details_prior_row_live_birth_badge')}
            </Pill>
          )}
        </div>
      </header>

      <div className="mt-3">
        <KeyValueGrid
          rows={
            isAnomaly
              ? [
                  { label: t('dossier_place'), value: placeLabel },
                  {
                    label: t('dossier_management_mode'),
                    value: labelForExtractionLike(row, t),
                  },
                  {
                    label: t('dossier_description'),
                    value: description.free || null,
                  },
                ]
              : [
                  { label: t('dossier_place'), value: placeLabel },
                  {
                    label: t('dossier_extraction'),
                    value: labelForExtractionLike(row, t),
                  },
                  {
                    label: t('dossier_cesarean_indication'),
                    value: row.cesarean_indication || null,
                  },
                  {
                    label: t('dossier_newborn_sex'),
                    value: codeLabel(NEWBORN_SEX_OPTIONS, row.newborn_sex, t),
                  },
                  {
                    label: t('dossier_vital_status'),
                    value: codeLabel(
                      NEWBORN_VITAL_STATUS_OPTIONS,
                      row.newborn_vital_status,
                      t
                    ),
                  },
                  {
                    label: t('dossier_newborn_weight'),
                    value:
                      row.newborn_weight_grams != null
                        ? `${row.newborn_weight_grams} g`
                        : null,
                  },
                  {
                    label: t('dossier_maternal_complications'),
                    value: codeLabel(
                      MATERNAL_COMPLICATION_OPTIONS,
                      row.delivery_complication_type,
                      t
                    ),
                  },
                  {
                    label: t('dossier_neonatal_complications'),
                    value: codeLabel(
                      NEONATAL_COMPLICATION_OPTIONS,
                      row.newborn_complication_type,
                      t
                    ),
                  },
                ]
          }
        />
      </div>
    </article>
  );
};

interface PriorDeliveriesCardProps {
  rows: PriorDeliveryApiRow[];
  isLoading?: boolean;
  error?: unknown;
}

const PriorDeliveriesCard: React.FC<PriorDeliveriesCardProps> = ({
  rows,
  isLoading,
  error,
}) => {
  const { t } = useTranslation('Patient');
  const summary = (() => {
    if (!rows.length) {
      return null;
    }
    const total = rows.length;
    const withAnomaly = rows.filter((r) => r.has_obstetric_anomalies).length;
    const live = total - withAnomaly;
    return { total, withAnomaly, live };
  })();

  return (
    <SectionCard
      icon={<FaPersonPregnant className="h-4 w-4" />}
      title={t('details_prior_section_title')}
      description={t('details_prior_section_helper')}
      collapsible
      defaultExpanded={false}
      action={
        summary ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <Pill tone="success">
              {t('details_prior_summary_delivered', { count: summary.live })}
            </Pill>
            <Pill tone="warning">
              {t('details_prior_summary_anomaly', {
                count: summary.withAnomaly,
              })}
            </Pill>
            <Pill tone="neutral">
              {t('details_prior_summary_total', { count: summary.total })}
            </Pill>
          </div>
        ) : null
      }
    >
      {isLoading ? (
        <EmptyState message={t('details_prior_loading')} />
      ) : error ? (
        <EmptyState
          icon={<FiAlertTriangle className="h-4 w-4 text-amber-500" />}
          message={t('details_prior_error')}
        />
      ) : rows.length === 0 ? (
        <EmptyState message={t('details_prior_empty')} />
      ) : (
        <div className="space-y-3">
          {rows.map((row, idx) => (
            <PriorDeliveryRowCard key={row.id ?? idx} row={row} />
          ))}
        </div>
      )}
    </SectionCard>
  );
};

const PregnancySnapshotCard: React.FC<{
  patient: PatientLike;
  supplementalContent?: React.ReactNode;
}> = ({ patient, supplementalContent }) => {
  const { t } = useTranslation('Patient');
  const { t: tPM } = useTranslation('PatientManagement');
  const pregnancy = patient.pregnancy as Record<string, unknown> | null;

  if (!pregnancy || pregnancy.id == null) {
    return (
      <SectionCard
        icon={<FaBaby className="h-4 w-4" />}
        title={t('details_current_section_title')}
        description={t('details_current_section_helper')}
      >
        <div className="space-y-4">
          <EmptyState message={t('details_current_empty')} />
          {supplementalContent}
        </div>
      </SectionCard>
    );
  }

  const lmp = formatDate(pregnancy.last_menstrual_period as string);
  const edd = formatDate(pregnancy.estimated_due_date as string);
  const status = String(pregnancy.pregnancy_status ?? 'Active');
  const eddPast = (() => {
    if (!pregnancy.estimated_due_date) {
      return false;
    }
    const d = dayjs(pregnancy.estimated_due_date as string);
    return d.isValid() && dayjs().startOf('day').isAfter(d.startOf('day'));
  })();

  const statusLabel = (() => {
    const map: Record<string, string> = {
      Active: 'timeline.statusLabels.active',
      Delivered: 'timeline.statusLabels.delivered',
      Miscarried: 'timeline.statusLabels.miscarried',
      Terminated: 'timeline.statusLabels.terminated',
      Unknown: 'timeline.statusLabels.unknown',
    };
    const key = map[status];
    return key ? tPM(key) : status;
  })();

  const ga = (() => {
    const weeks =
      asNumber(pregnancy.current_gestational_age_weeks) ??
      asNumber(pregnancy.gestational_age_weeks);
    const days =
      asNumber(pregnancy.current_gestational_age_days) ??
      asNumber(pregnancy.gestational_age_days);

    if (weeks != null) {
      return `${weeks}+${days ?? 0}`;
    }

    if (typeof pregnancy.last_menstrual_period === 'string') {
      const computed = computeGAFromLmp(
        pregnancy.last_menstrual_period,
        typeof pregnancy.ultrasound_date === 'string'
          ? pregnancy.ultrasound_date
          : undefined
      );
      if (computed) {
        return `${computed.weeks}+${computed.days}`;
      }
    }

    return null;
  })();

  const highRiskClassification =
    typeof pregnancy.grossesse_classee_a_risque === 'boolean'
      ? pregnancy.grossesse_classee_a_risque
      : null;
  const selectedRiskTypes = PREGNANCY_RISK_OPTIONS.filter(
    (option) => pregnancy[option.value] === true
  );
  const otherRiskDetails =
    typeof pregnancy.risque_autre_details === 'string'
      ? pregnancy.risque_autre_details.trim()
      : '';

  return (
    <SectionCard
      icon={<FaBaby className="h-4 w-4" />}
      title={t('details_current_section_title')}
      description={t('details_current_section_helper')}
      action={
        <div className="flex flex-wrap items-center gap-1.5">
          <Pill tone={status === 'Active' ? 'success' : 'neutral'}>
            {statusLabel}
          </Pill>
          {eddPast && status === 'Active' ? (
            <Pill tone="warning">{t('details_current_edd_passed_badge')}</Pill>
          ) : null}
        </div>
      }
    >
      <div className="space-y-4">
        <KeyValueGrid
          maxCols={4}
          rows={[
            { label: t('dossier_lmp_short'), value: lmp },
            { label: t('dossier_edd_short'), value: edd },
            { label: t('dossier_gestational_age'), value: ga },
            {
              label: t('dossier_dating_method'),
              value: (pregnancy.dating_method as string | null) || null,
            },
          ]}
        />
        <div
          className={`rounded-xl border p-4 ${
            highRiskClassification === true
              ? 'border-rose-200 bg-rose-50/70 dark:border-rose-800/70 dark:bg-rose-950/30'
              : highRiskClassification === false
                ? 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-800/70 dark:bg-emerald-950/25'
                : 'border-slate-200 bg-card-color-200/50 dark:border-slate-700 dark:bg-gray-900/40'
          }`}
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-title-page-color-300 dark:text-gray-400">
                {t('dossier_pregnancy_high_risk')}
              </p>
              <p className="mt-1 text-sm text-title-page-color-300 dark:text-gray-400">
                {highRiskClassification === true
                  ? tPM('timeline.currentPregnancy.highRiskConfirmed')
                  : highRiskClassification === false
                    ? tPM('timeline.currentPregnancy.highRiskNotClassified')
                    : tPM('timeline.currentPregnancy.highRiskNotDocumented')}
              </p>
            </div>
            <Pill
              tone={
                highRiskClassification === true
                  ? 'danger'
                  : highRiskClassification === false
                    ? 'success'
                    : 'neutral'
              }
            >
              {highRiskClassification === true
                ? t('yes')
                : highRiskClassification === false
                  ? t('no')
                  : tPM('timeline.currentPregnancy.notDocumented')}
            </Pill>
          </div>

          {highRiskClassification === true ? (
            <div className="mt-3 space-y-3 border-t border-current/10 pt-3">
              {selectedRiskTypes.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {selectedRiskTypes.map((option) => (
                    <Pill key={option.value} tone="danger">
                      {t(option.labelKey)}
                    </Pill>
                  ))}
                </div>
              ) : (
                <p className="text-xs italic text-title-page-color-300 dark:text-gray-400">
                  {tPM('timeline.currentPregnancy.noRiskTypesDocumented')}
                </p>
              )}
              {otherRiskDetails ? (
                <div className="rounded-lg bg-white/70 px-3 py-2 dark:bg-gray-900/50">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-title-page-color-300 dark:text-gray-400">
                    {t('dossier_pregnancy_risk_other')}
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-title-page-color-100 dark:text-gray-200">
                    {otherRiskDetails}
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        {supplementalContent}
      </div>
    </SectionCard>
  );
};

export interface PatientDossierPayload {
  patient?: PatientLike | null;
  pregnancy?: Record<string, unknown> | null;
  family_history?: FamilyHistoryRow[] | null;
  prior_deliveries?: PriorDeliveryApiRow[] | null;
  patient_medical_history?: MedicalHistoryRow | null;
}

export interface PatientDetailsViewProps {
  patientId: string;
  patient?: PatientLike | null;
  dossier?: PatientDossierPayload | null;
  isDossierLoading?: boolean;
  dossierError?: unknown;
  pregnancySupplementalContent?: React.ReactNode;
}

export const unwrapPatientDossier = (raw: unknown): PatientDossierPayload => {
  if (!raw || typeof raw !== 'object') {
    return {};
  }
  const wrapper = raw as { data?: unknown };
  const payload =
    wrapper.data !== undefined && wrapper.data !== null ? wrapper.data : raw;
  if (!payload || typeof payload !== 'object') {
    return {};
  }
  return payload as PatientDossierPayload;
};

const PatientDetailsView: React.FC<PatientDetailsViewProps> = ({
  patientId,
  patient: fallbackPatient,
  dossier: externalDossier,
  isDossierLoading: externalLoading,
  dossierError: externalError,
  pregnancySupplementalContent,
}) => {
  const { t } = useTranslation('Patient');
  const driveOwnFetch = externalDossier === undefined;
  const {
    data,
    isLoading: hookLoading,
    error: hookError,
  } = usePatientDossier(driveOwnFetch ? patientId : undefined);
  const dossier: PatientDossierPayload = driveOwnFetch
    ? unwrapPatientDossier(data)
    : (externalDossier ?? {});
  const isLoading = driveOwnFetch ? hookLoading : Boolean(externalLoading);
  const error = driveOwnFetch ? hookError : externalError;

  const patient: PatientLike | null = (() => {
    if (dossier.patient && typeof dossier.patient === 'object') {
      const merged = { ...dossier.patient } as PatientLike;
      if (
        dossier.pregnancy &&
        typeof dossier.pregnancy === 'object' &&
        merged.pregnancy == null
      ) {
        merged.pregnancy = dossier.pregnancy as Record<string, unknown>;
      }
      return merged;
    }
    return fallbackPatient ?? null;
  })();

  const familyRows: FamilyHistoryRow[] = Array.isArray(dossier.family_history)
    ? (dossier.family_history as FamilyHistoryRow[])
    : [];
  const priorRows: PriorDeliveryApiRow[] = Array.isArray(
    dossier.prior_deliveries
  )
    ? (dossier.prior_deliveries as PriorDeliveryApiRow[])
    : [];
  const medicalHistoryRow: MedicalHistoryRow =
    dossier.patient_medical_history &&
    typeof dossier.patient_medical_history === 'object'
      ? (dossier.patient_medical_history as MedicalHistoryRow)
      : {};
  if (!patient && isLoading) {
    return <EmptyState message={t('details_dossier_loading')} />;
  }

  if (!patient) {
    return <EmptyState message={t('details_dossier_not_loaded')} />;
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-5 overflow-y-auto overscroll-y-contain pr-1 [scrollbar-gutter:stable]">
      <DemographicsCard patient={patient} />
      <div className="grid shrink-0 grid-cols-1 gap-5 xl:grid-cols-3">
        <HabitsCard patient={patient} />
        <BloodGroupCard patient={patient} />
        <AnthropometryCard patient={patient} />
      </div>
      <PregnancySnapshotCard
        patient={patient}
        supplementalContent={pregnancySupplementalContent}
      />
      <FamilyHistoryCard
        rows={familyRows}
        isLoading={isLoading}
        error={error}
      />
      <WomansHistoryCard
        row={medicalHistoryRow}
        isLoading={isLoading}
        error={error}
      />
      <PriorDeliveriesCard
        rows={priorRows}
        isLoading={isLoading}
        error={error}
      />
    </div>
  );
};

export default PatientDetailsView;
